import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { fetchAdminReports, type AdminReportsResponse } from "../../lib/admin-reports";
import { formatCurrency } from "../../lib/utils";

const emptyReports: AdminReportsResponse = {
  summary: {
    paidRevenueTotal: 0,
    paidOrdersTotal: 0,
    currentMonthRevenue: 0,
    currentMonthPaidOrders: 0,
    unpaidOrdersTotal: 0,
    outstandingBalanceTotal: 0,
    discountsTotal: 0,
    customerCount: 0
  },
  monthlySeries: [],
  dailySeries: [],
  topProducts: [],
  categoryPerformance: [],
  couponPerformance: [],
  paymentMix: []
};

function buildLinePath(values: number[], width: number, height: number) {
  if (values.length === 0) {
    return "";
  }

  const max = Math.max(...values, 1);
  return values
    .map((value, index) => {
      const x = values.length === 1 ? width / 2 : (index / (values.length - 1)) * width;
      const y = height - (value / max) * height;
      return `${index === 0 ? "M" : "L"} ${x.toFixed(2)} ${y.toFixed(2)}`;
    })
    .join(" ");
}

export function AdminReportsPage() {
  const [data, setData] = useState<AdminReportsResponse>(emptyReports);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeMonthIndex, setActiveMonthIndex] = useState<number>(-1);

  useEffect(() => {
    let ignore = false;

    async function load() {
      setLoading(true);
      setError(null);

      try {
        const result = await fetchAdminReports();
        if (!ignore) {
          setData(result);
        }
      } catch (loadError) {
        if (!ignore) {
          setError(loadError instanceof Error ? loadError.message : "Unable to load reports.");
        }
      } finally {
        if (!ignore) {
          setLoading(false);
        }
      }
    }

    void load();

    return () => {
      ignore = true;
    };
  }, []);

  const metricCards = useMemo(
    () => [
      {
        label: "Paid revenue",
        value: formatCurrency(data.summary.paidRevenueTotal),
        detail: `${data.summary.paidOrdersTotal} paid orders`
      },
      {
        label: "This month",
        value: formatCurrency(data.summary.currentMonthRevenue),
        detail: `${data.summary.currentMonthPaidOrders} paid orders`
      },
      {
        label: "Outstanding",
        value: formatCurrency(data.summary.outstandingBalanceTotal),
        detail: `${data.summary.unpaidOrdersTotal} unpaid orders`
      },
      {
        label: "Customers reached",
        value: String(data.summary.customerCount),
        detail: `${formatCurrency(data.summary.discountsTotal)} discounts issued`
      }
    ],
    [data.summary]
  );

  const monthlyValues = data.monthlySeries.map((item) => item.paidRevenue);
  const monthlyPath = useMemo(() => buildLinePath(monthlyValues, 620, 220), [monthlyValues]);
  const resolvedMonthIndex =
    activeMonthIndex >= 0 && activeMonthIndex < data.monthlySeries.length ? activeMonthIndex : data.monthlySeries.length - 1;
  const activeMonth = resolvedMonthIndex >= 0 ? data.monthlySeries[resolvedMonthIndex] : null;
  const maxDailyRevenue = Math.max(...data.dailySeries.map((item) => item.paidRevenue), 1);
  const categoryRevenueBase = Math.max(...data.categoryPerformance.map((item) => item.paidRevenue), 1);
  const paymentRevenueBase = Math.max(...data.paymentMix.map((item) => item.paidRevenue), 1);

  return (
    <div className="space-y-6 pb-6">
      <div className="relative overflow-hidden rounded-[36px] border border-white/10 bg-slate-950 px-6 py-7 text-white">
        <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(circle_at_top_left,rgba(16,185,129,0.16),transparent_34%),radial-gradient(circle_at_85%_10%,rgba(14,165,233,0.18),transparent_30%),linear-gradient(135deg,rgba(15,23,42,0.96),rgba(3,7,18,0.98))]" />
        <div className="relative flex flex-wrap items-end justify-between gap-5">
          <div>
            <p className="text-sm uppercase tracking-[0.2em] text-emerald-300">Reports</p>
            <h1 className="mt-3 text-4xl font-semibold tracking-tight">Commercial reporting</h1>
            <p className="mt-4 max-w-3xl text-sm leading-7 text-slate-300">
              Revenue movement, product demand, coupon impact, and payment behavior in one surface.
            </p>
          </div>
          <div className="grid gap-3 sm:grid-cols-3">
            <Link className="rounded-full border border-white/15 bg-white/8 px-4 py-2 text-sm font-semibold transition hover:bg-white/14" to="/admin/orders">
              Review orders
            </Link>
            <Link className="rounded-full border border-white/15 bg-white/8 px-4 py-2 text-sm font-semibold transition hover:bg-white/14" to="/admin/catalog">
              Inspect catalog
            </Link>
            <Link className="rounded-full border border-white/15 bg-white/8 px-4 py-2 text-sm font-semibold transition hover:bg-white/14" to="/admin/coupons">
              Audit coupons
            </Link>
          </div>
        </div>
      </div>

      {error ? <div className="rounded-2xl border border-red-200 bg-red-50 p-4 text-sm text-red-700 dark:border-red-900/70 dark:bg-red-950/40 dark:text-red-300">{error}</div> : null}

      <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
        {metricCards.map((metric) => (
          <div className="admin-card rounded-[30px] p-5" key={metric.label}>
            <p className="text-xs font-semibold uppercase tracking-[0.22em] text-nv-slate">{metric.label}</p>
            <p className="mt-4 break-words text-3xl font-semibold tracking-tight text-nv-ink">{metric.value}</p>
            <p className="mt-3 text-sm text-nv-slate">{metric.detail}</p>
          </div>
        ))}
      </div>

      <div className="grid items-start gap-4 xl:grid-cols-[minmax(0,1.45fr)_minmax(320px,0.95fr)]">
        <div className="admin-card min-w-0 rounded-[32px] p-6">
          <div className="flex flex-wrap items-end justify-between gap-4">
            <div>
              <p className="text-sm uppercase tracking-[0.2em] text-emerald-300">Revenue trend</p>
              <h2 className="mt-2 text-2xl font-semibold tracking-tight text-nv-ink">Paid revenue over the last twelve months</h2>
            </div>
            <div className="text-right">
              <p className="text-xs font-semibold uppercase tracking-[0.2em] text-nv-slate">
                {activeMonth ? `Selected month: ${activeMonth.monthLabel}` : "Latest month"}
              </p>
              <p className="mt-2 text-xl font-semibold text-nv-ink">
                {activeMonth ? formatCurrency(activeMonth.paidRevenue) : formatCurrency(0)}
              </p>
              <p className="mt-1 text-xs text-nv-slate">{activeMonth ? `${activeMonth.paidOrders} paid orders` : "No paid revenue yet"}</p>
            </div>
          </div>

          <div className="mt-6 rounded-[28px] border border-black/6 bg-[linear-gradient(180deg,rgba(16,185,129,0.08),rgba(255,255,255,0))] p-4 dark:border-white/8">
            {loading ? (
              <div className="flex h-[270px] items-center justify-center text-sm text-nv-slate">Loading reports...</div>
            ) : data.monthlySeries.length > 0 ? (
              <div className="min-w-0">
                <svg className="h-[270px] w-full" preserveAspectRatio="none" role="img" viewBox="0 0 620 270">
                  <defs>
                    <linearGradient id="reportsRevenueStroke" x1="0%" x2="100%" y1="0%" y2="0%">
                      <stop offset="0%" stopColor="#10b981" />
                      <stop offset="100%" stopColor="#0ea5e9" />
                    </linearGradient>
                    <linearGradient id="reportsRevenueFill" x1="0%" x2="0%" y1="0%" y2="100%">
                      <stop offset="0%" stopColor="rgba(16,185,129,0.24)" />
                      <stop offset="100%" stopColor="rgba(14,165,233,0.02)" />
                    </linearGradient>
                  </defs>
                  {[0, 1, 2, 3].map((line) => (
                    <line
                      key={line}
                      x1="0"
                      x2="620"
                      y1={24 + line * 60}
                      y2={24 + line * 60}
                      stroke="rgba(90,103,120,0.18)"
                      strokeDasharray="4 8"
                    />
                  ))}
                  <path d={`${monthlyPath} L 620 220 L 0 220 Z`} fill="url(#reportsRevenueFill)" transform="translate(0 24)" />
                  <path d={monthlyPath} fill="none" stroke="url(#reportsRevenueStroke)" strokeLinecap="round" strokeWidth="4" transform="translate(0 24)" />
                  {data.monthlySeries.map((point, index) => {
                    const max = Math.max(...monthlyValues, 1);
                    const x = data.monthlySeries.length === 1 ? 310 : (index / (data.monthlySeries.length - 1)) * 620;
                    const y = 24 + (220 - (point.paidRevenue / max) * 220);
                    const active = index === resolvedMonthIndex;
                    return (
                      <g key={point.monthKey}>
                        <circle
                          cx={x}
                          cy={y}
                          fill="transparent"
                          onFocus={() => setActiveMonthIndex(index)}
                          onMouseEnter={() => setActiveMonthIndex(index)}
                          r="16"
                          tabIndex={0}
                        />
                        <circle
                          cx={x}
                          cy={y}
                          fill="#ffffff"
                          r={active ? "7" : "5"}
                          stroke={active ? "#10b981" : "#0f172a"}
                          strokeWidth={active ? "4" : "3"}
                        />
                      </g>
                    );
                  })}
                </svg>
                <div className="mt-3 grid grid-cols-2 gap-2 text-xs font-semibold uppercase tracking-[0.18em] text-nv-slate sm:grid-cols-3 xl:grid-cols-6">
                  {data.monthlySeries.map((point, index) => (
                    <button
                      className={`min-w-0 rounded-2xl px-2 py-2 text-left transition ${
                        index === resolvedMonthIndex ? "bg-black/[0.06] text-nv-ink dark:bg-white/[0.08]" : ""
                      }`}
                      key={point.monthKey}
                      onClick={() => setActiveMonthIndex(index)}
                      onMouseEnter={() => setActiveMonthIndex(index)}
                      type="button"
                    >
                      <p>{point.monthLabel}</p>
                      <p className="mt-1 break-words normal-case tracking-normal text-[11px] text-nv-ink">{formatCurrency(point.paidRevenue)}</p>
                    </button>
                  ))}
                </div>
              </div>
            ) : (
              <div className="flex h-[270px] items-center justify-center text-sm text-nv-slate">No paid revenue history yet.</div>
            )}
          </div>
        </div>

        <div className="admin-card rounded-[32px] p-6">
          <p className="text-sm uppercase tracking-[0.2em] text-emerald-300">Recent momentum</p>
          <h2 className="mt-2 text-2xl font-semibold tracking-tight text-nv-ink">Last 14 days</h2>
          <div className="mt-6 space-y-3">
            {loading ? (
              <div className="text-sm text-nv-slate">Loading daily sales...</div>
            ) : data.dailySeries.length > 0 ? (
              data.dailySeries.map((day) => (
                <div className="grid grid-cols-[64px_minmax(0,1fr)_88px] items-center gap-3" key={day.dayKey}>
                  <span className="text-xs font-semibold uppercase tracking-[0.16em] text-nv-slate">{day.dayLabel}</span>
                  <div className="h-3 overflow-hidden rounded-full bg-black/[0.06] dark:bg-white/[0.08]">
                    <div
                      className="h-full rounded-full bg-gradient-to-r from-emerald-500 to-sky-500"
                      style={{ width: `${Math.max((day.paidRevenue / maxDailyRevenue) * 100, day.paidRevenue > 0 ? 8 : 0)}%` }}
                    />
                  </div>
                  <span className="text-right text-sm font-semibold text-nv-ink">{day.paidOrders}</span>
                </div>
              ))
            ) : (
              <div className="text-sm text-nv-slate">No recent paid sales yet.</div>
            )}
          </div>
          <div className="mt-5 rounded-2xl border border-black/6 bg-black/[0.03] px-4 py-3 text-sm text-nv-slate dark:border-white/8 dark:bg-white/[0.04]">
            Bars reflect paid revenue by day. The number at the right is paid-order count.
          </div>
        </div>
      </div>

      <div className="grid items-start gap-4 xl:grid-cols-[minmax(0,1.1fr)_minmax(0,0.9fr)]">
        <div className="admin-card min-w-0 rounded-[32px] p-6">
          <div className="flex flex-wrap items-end justify-between gap-4">
            <div>
              <p className="text-sm uppercase tracking-[0.2em] text-emerald-300">Demand leaders</p>
              <h2 className="mt-2 text-2xl font-semibold tracking-tight text-nv-ink">Top products by paid revenue</h2>
            </div>
            <Link className="text-sm font-semibold text-cyan-700 hover:text-cyan-800 dark:text-cyan-300" to="/admin/catalog">
              Open catalog
            </Link>
          </div>
          <div className="mt-6 overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead className="text-left text-xs font-semibold uppercase tracking-[0.18em] text-nv-slate">
                <tr>
                  <th className="pb-3 pr-4">Product</th>
                  <th className="pb-3 pr-4">Category</th>
                  <th className="pb-3 pr-4 text-right">Units</th>
                  <th className="pb-3 pr-4 text-right">Orders</th>
                  <th className="pb-3 text-right">Revenue</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td className="py-4 text-nv-slate" colSpan={5}>
                      Loading top products...
                    </td>
                  </tr>
                ) : data.topProducts.length > 0 ? (
                  data.topProducts.map((product) => (
                    <tr className="border-t border-black/6 align-top dark:border-white/8" key={`${product.inventoryId}-${product.name}`}>
                      <td className="py-4 pr-4">
                        {product.inventoryId && product.slug ? (
                          <Link className="font-semibold text-nv-ink hover:text-cyan-700" to={`/shop/product/${product.slug}/${product.inventoryId}`}>
                            {product.name}
                          </Link>
                        ) : (
                          <span className="font-semibold text-nv-ink">{product.name}</span>
                        )}
                      </td>
                      <td className="py-4 pr-4 text-nv-slate">{product.category}</td>
                      <td className="py-4 pr-4 text-right font-semibold text-nv-ink">{product.unitsSold}</td>
                      <td className="py-4 pr-4 text-right text-nv-slate">{product.paidOrders}</td>
                      <td className="py-4 text-right font-semibold text-nv-ink">{formatCurrency(product.paidRevenue)}</td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td className="py-4 text-nv-slate" colSpan={5}>
                      No paid product performance yet.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>

        <div className="admin-card rounded-[32px] p-6">
          <p className="text-sm uppercase tracking-[0.2em] text-emerald-300">Category mix</p>
          <h2 className="mt-2 text-2xl font-semibold tracking-tight text-nv-ink">Revenue by category</h2>
          <div className="mt-6 space-y-4">
            {loading ? (
              <div className="text-sm text-nv-slate">Loading category performance...</div>
            ) : data.categoryPerformance.length > 0 ? (
              data.categoryPerformance.map((item) => (
                <div key={item.category}>
                  <div className="mb-2 flex items-center justify-between gap-3">
                    <div>
                      <p className="font-semibold text-nv-ink">{item.category}</p>
                      <p className="text-xs uppercase tracking-[0.14em] text-nv-slate">{item.unitsSold} units sold</p>
                    </div>
                    <div className="text-right">
                      <p className="font-semibold text-nv-ink">{formatCurrency(item.paidRevenue)}</p>
                      <p className="text-xs text-nv-slate">{item.paidOrders} orders</p>
                    </div>
                  </div>
                  <div className="h-3 overflow-hidden rounded-full bg-black/[0.06] dark:bg-white/[0.08]">
                    <div
                      className="h-full rounded-full bg-gradient-to-r from-cyan-500 to-emerald-500"
                      style={{ width: `${Math.max((item.paidRevenue / categoryRevenueBase) * 100, item.paidRevenue > 0 ? 10 : 0)}%` }}
                    />
                  </div>
                </div>
              ))
            ) : (
              <div className="text-sm text-nv-slate">No category revenue yet.</div>
            )}
          </div>
        </div>
      </div>

      <div className="grid items-start gap-4 xl:grid-cols-[minmax(0,1fr)_minmax(0,1fr)]">
        <div className="admin-card min-w-0 rounded-[32px] p-6">
          <div className="flex flex-wrap items-end justify-between gap-4">
            <div>
              <p className="text-sm uppercase tracking-[0.2em] text-emerald-300">Coupon impact</p>
              <h2 className="mt-2 text-2xl font-semibold tracking-tight text-nv-ink">Promotion performance</h2>
            </div>
            <Link className="text-sm font-semibold text-cyan-700 hover:text-cyan-800 dark:text-cyan-300" to="/admin/coupons">
              Manage coupons
            </Link>
          </div>
          <div className="mt-6 overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead className="text-left text-xs font-semibold uppercase tracking-[0.18em] text-nv-slate">
                <tr>
                  <th className="pb-3 pr-4">Coupon</th>
                  <th className="pb-3 pr-4 text-right">Uses</th>
                  <th className="pb-3 pr-4 text-right">Discounts</th>
                  <th className="pb-3 text-right">Revenue</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td className="py-4 text-nv-slate" colSpan={4}>
                      Loading coupon performance...
                    </td>
                  </tr>
                ) : data.couponPerformance.length > 0 ? (
                  data.couponPerformance.map((coupon) => (
                    <tr className="border-t border-black/6 align-top dark:border-white/8" key={`${coupon.couponId}-${coupon.code}`}>
                      <td className="py-4 pr-4">
                        <p className="font-semibold text-nv-ink">{coupon.code}</p>
                        {coupon.description ? <p className="mt-1 text-xs text-nv-slate">{coupon.description}</p> : null}
                      </td>
                      <td className="py-4 pr-4 text-right font-semibold text-nv-ink">{coupon.usesTotal}</td>
                      <td className="py-4 pr-4 text-right text-nv-slate">{formatCurrency(coupon.discountTotal)}</td>
                      <td className="py-4 text-right font-semibold text-nv-ink">{formatCurrency(coupon.paidRevenue)}</td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td className="py-4 text-nv-slate" colSpan={4}>
                      No coupon-driven paid orders yet.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>

        <div className="admin-card rounded-[32px] p-6">
          <p className="text-sm uppercase tracking-[0.2em] text-emerald-300">Payment mix</p>
          <h2 className="mt-2 text-2xl font-semibold tracking-tight text-nv-ink">Paid revenue by payment type</h2>
          <div className="mt-6 space-y-4">
            {loading ? (
              <div className="text-sm text-nv-slate">Loading payment mix...</div>
            ) : data.paymentMix.length > 0 ? (
              data.paymentMix.map((item) => (
                <div className="rounded-2xl border border-black/6 p-4 dark:border-white/8" key={item.paymentType}>
                  <div className="flex items-center justify-between gap-3">
                    <div>
                      <p className="font-semibold text-nv-ink">{item.paymentType}</p>
                      <p className="text-xs uppercase tracking-[0.14em] text-nv-slate">{item.ordersTotal} paid orders</p>
                    </div>
                    <p className="font-semibold text-nv-ink">{formatCurrency(item.paidRevenue)}</p>
                  </div>
                  <div className="mt-3 h-3 overflow-hidden rounded-full bg-black/[0.06] dark:bg-white/[0.08]">
                    <div
                      className="h-full rounded-full bg-gradient-to-r from-sky-500 to-cyan-500"
                      style={{ width: `${Math.max((item.paidRevenue / paymentRevenueBase) * 100, item.paidRevenue > 0 ? 10 : 0)}%` }}
                    />
                  </div>
                </div>
              ))
            ) : (
              <div className="text-sm text-nv-slate">No payment data yet.</div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
